import { useState } from 'react'

import { t } from '@molecule/app-i18n'
import { getClassMap } from '@molecule/app-ui'

import { Icon } from './Icon.js'
import { MoleculeSpinner } from './MoleculeSpinner.js'

/**
 * Full-bleed overlay shown over the live preview when the SERVER-SIDE health probe reports
 * the preview is `down` (see {@link usePreviewHealth}). Deliberately CALM and reassuring: a
 * non-technical user should feel that things are being handled, not be confronted with a
 * scary "crashed" banner and a wall of stack-trace text. So by default it shows only the
 * molecule spinner + "Synthase is fixing the preview…". The actual (secret-redacted) probe
 * error is tucked away behind a "More details" toggle, collapsed by default, for the curious
 * or technical user who wants to see exactly what broke.
 *
 * Two moods, chosen by whether Synthase is (or is about to be) on it:
 *  - `fixing` (the project's /autofix is on, OR a Synthase turn is already running) → the
 *    spinner + reassuring copy, no button. This is the common case and the one the user asked
 *    for: just "Synthase is fixing the preview…".
 *  - otherwise (autofix off AND nothing running) → a gentle prompt + a single "Fix with AI"
 *    button so the user can hand the break off in one click.
 *
 * The details toggle's open/closed state is component-local; the parent remounts this via
 * `key={downSeq}` on each fresh down episode, so a previously-expanded error never carries
 * over into the next break — the calm view is always the default.
 *
 * @param root0 - Component props.
 * @param root0.message - The secret-redacted probe error, if any (shown only when expanded).
 * @param root0.autoFix - The project's /autofix setting — when on, Synthase fixes it automatically.
 * @param root0.aiBuilding - Whether a Synthase turn is currently running.
 * @param root0.onFixWithAI - Hand the break to Synthase (only reachable when not already fixing).
 * @returns The rendered broken-preview overlay.
 */
export function PreviewBrokenOverlay({
  message,
  autoFix,
  aiBuilding,
  onFixWithAI,
}: {
  message?: string
  autoFix: boolean
  aiBuilding: boolean
  onFixWithAI: () => void
}) {
  const cm = getClassMap()
  const [showDetails, setShowDetails] = useState(false)
  // Synthase is (or will be) on it whenever /autofix is on or a turn is already running.
  // Only when neither holds does the user need to act — the sole case with a Fix button.
  const fixing = autoFix || aiBuilding

  return (
    // role="status" + aria-live="polite" (NOT the old assertive role="alert"): this is a
    // "we're on it" status, not an emergency, so it should be announced calmly.
    <div
      role="status"
      aria-live="polite"
      data-mol-id="preview-broken-overlay"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 24,
        textAlign: 'center',
        background: 'var(--mol-color-surface, #0e0e0e)',
        color: 'var(--mol-color-text, currentColor)',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {fixing ? (
        <MoleculeSpinner size="lg" />
      ) : (
        <span style={{ display: 'inline-flex', opacity: 0.55 }}>
          <Icon name="info-circle" size={30} />
        </span>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 380 }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>
          {fixing
            ? t(
                'ide.preview.broken.fixing',
                {},
                { defaultValue: 'Synthase is fixing the preview…' },
              )
            : t(
                'ide.preview.broken.needsFix',
                {},
                { defaultValue: 'The preview needs a quick fix' },
              )}
        </div>
        <div style={{ fontSize: 13, opacity: 0.7, lineHeight: 1.5 }}>
          {fixing
            ? t(
                'ide.preview.broken.fixingHint',
                {},
                { defaultValue: 'Hang tight — this usually only takes a moment.' },
              )
            : t(
                'ide.preview.broken.needsFixHint',
                {},
                { defaultValue: 'Let Synthase find what went wrong and fix it for you.' },
              )}
        </div>
      </div>

      {!fixing && (
        // The overlay's one action — solid primary, per DESIGN.md's
        // "Actions look like buttons" (solid for the surface's one primary action).
        <button
          type="button"
          data-mol-id="preview-broken-fix-button"
          onClick={onFixWithAI}
          className={cm.cn(
            cm.button({ variant: 'solid', color: 'primary', size: 'sm' }),
            cm.touchTargetCompact,
          )}
        >
          {t('ide.preview.broken.fixWithAi', {}, { defaultValue: 'Fix with AI' })}
        </button>
      )}

      {/* The real error, hidden by default. A subtle "(?) More details" disclosure keeps the
          front-of-house calm while still letting a technical user see exactly what broke. */}
      {message && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            width: '100%',
          }}
        >
          {/* A show/hide disclosure — `ghost`, the one variant DESIGN.md reserves
              for dismiss/close controls and show/hide toggles. */}
          <button
            type="button"
            data-mol-id="preview-broken-details-toggle"
            aria-expanded={showDetails}
            onClick={() => setShowDetails((open) => !open)}
            className={cm.cn(
              cm.button({ variant: 'ghost', size: 'xs' }),
              cm.textMuted,
              cm.touchTargetCompact,
            )}
            style={{ opacity: 0.75 }}
          >
            <Icon name="question" size={13} />
            {showDetails
              ? t('ide.preview.broken.hideDetails', {}, { defaultValue: 'Hide details' })
              : t('ide.preview.broken.moreDetails', {}, { defaultValue: 'More details' })}
            <Icon name={showDetails ? 'chevron-up' : 'chevron-down'} size={12} />
          </button>
          {showDetails && (
            <pre
              data-mol-id="preview-broken-details"
              style={{
                fontSize: 12,
                lineHeight: 1.45,
                opacity: 0.85,
                width: '100%',
                maxWidth: 560,
                maxHeight: 180,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                textAlign: 'left',
                margin: 0,
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid var(--mol-color-border, rgba(128,128,128,0.25))',
                background: 'var(--mol-color-surface-secondary, rgba(128,128,128,0.08))',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {message}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}
